import React, { useRef, useEffect } from 'react';
import { Box, CircularProgress, Grid, Stack, styled, Typography, useTheme } from '@mui/material';
import { t } from 'i18next';
import Timer from '@shared/components/timer/timer';
import { STOP_AUTOSCROLL_THRESHOLD_PX } from '../constants';
import { ConversationListMessage } from '../ConversationListMessage';
import { CallActors } from '@shared/constants/recipient-status.constants';
import { IconDownload } from '@tabler/icons-react';
import { ActionTableButton } from '@shared/components/actionTableButton/actionTableButton';
import { DocMessage } from '@features/Admin/Campaign/model/type';
import { downloadDialogAsPdf } from '@features/Admin/Campaign/utils/js-pdf.util';
import { useConversationMessageListControllerGetConversationsQuery } from '@shared/services/conversationApiService/apiService';
import { useStyles } from '../styles';
import { ConversationContentProps } from '../types';
import { useConversationMessages, useCallDetails } from '../hooks';
import { WrappedSentimentFlag } from './WrappedSentimentFlag';

const StyledCallInfoStack = styled(Stack)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
}));

/* eslint-disable react/prop-types */
export const ConversationContent: React.FC<ConversationContentProps> = ({
  call,
  phoneNumber,
  startCallTime,
  onError,
  isRoomConnected,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesBoxRef = useRef<HTMLDivElement | null>(null);

  const { callDuration } = useCallDetails(call, startCallTime);
  const { sentiments, id: callId } = call;

  const { data: conversationDialog } = useConversationMessageListControllerGetConversationsQuery({
    conversationId: call.id,
  });

  const { allMessages, loading } = useConversationMessages(callId, isRoomConnected, onError);
  const hasMessages = allMessages.length > 0;
  const isActiveCall = call.status !== 'ended';
  const showWaitingState = !hasMessages && (loading || isActiveCall);

  useEffect(() => {
    if (!messagesBoxRef.current || !messagesEndRef.current) return;

    const { clientHeight, scrollTop, scrollHeight } = messagesBoxRef.current;
    const isNearBottom = scrollHeight - STOP_AUTOSCROLL_THRESHOLD_PX <= clientHeight + scrollTop;

    if (isNearBottom) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages]);

  useEffect(() => {
    if (!messagesEndRef.current) return;

    if (!loading) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading]);

  const getcallStatusLine = (title: string, value: string | React.ReactNode) => (
    <StyledCallInfoStack direction="row" gap={1}>
      <Typography color={theme.palette.extendedGrey?.darker} fontWeight="500" fontSize="12px">
        {title}:
      </Typography>
      <Box color={theme.palette.extendedGrey?.darker} fontWeight="500" fontSize="12px">
        {value}
      </Box>
    </StyledCallInfoStack>
  );

  return (
    <Grid container className={classes.dialogGridContent} id="dialog-grid-content">
      <Grid item xs={12}>
        {callDuration && getcallStatusLine(t('campaignPage.duration'), callDuration)}
        {startCallTime &&
          callDuration &&
          getcallStatusLine(t('campaignPage.duration'), <Timer startTimestamp={startCallTime} />)}
      </Grid>
      <Grid item xs={12}>
        {callDuration &&
          getcallStatusLine(
            t('campaignPage.sentiment'),
            <WrappedSentimentFlag sentiments={sentiments} />,
          )}
        {startCallTime &&
          callDuration &&
          getcallStatusLine(
            t('campaignPage.sentiment'),
            <WrappedSentimentFlag sentiments={sentiments} />,
          )}
      </Grid>
      <Grid item xs={12}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <ActionTableButton
            onClick={() => {
              const number = call.customer?.number;
              downloadDialogAsPdf(
                number || CallActors.CUSTOMER,
                conversationDialog as DocMessage[],
              );
            }}>
            <IconDownload />
          </ActionTableButton>
        </Box>
      </Grid>
      <Grid item xs={12} mt="10px">
        <Box className={classes.chatBox}>
          {!loading && hasMessages ? (
            <Box ref={messagesBoxRef} className={classes.chatBoxMessages}>
              {allMessages.map((messageItem, index) => {
                let actorName: string | undefined;
                if (messageItem.actor === CallActors.AGENT) {
                  actorName = undefined;
                } else if ((messageItem as any).isMonitor) {
                  actorName = 'Monitor';
                } else if ((messageItem as any).isLive) {
                  actorName = `${phoneNumber} (Live)`;
                } else {
                  actorName = phoneNumber;
                }

                return (
                  <ConversationListMessage
                    key={messageItem.id || index}
                    actorName={actorName}
                    messageItem={messageItem}
                    isStacked={messageItem.actor === allMessages[index - 1]?.actor}
                  />
                );
              })}
              <div ref={messagesEndRef} />
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="400px">
              {showWaitingState ? (
                <>
                  <CircularProgress />
                  <Typography sx={{ textAlign: 'center', mt: 2 }}>
                    {t('campaignPage.conversationNotStartedYet')}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ textAlign: 'center' }}>
                  {t('campaignPage.emptyConversationHistory')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};
/* eslint-enable react/prop-types */
